/* ============================================================================
 * VesperSequence.jsx — the full three-act WebGL sequence, on an endless loop.
 *
 * Orb → spiral galaxy → point-cloud brain → burst → back to the orb.
 *
 * COPY THIS FILE VERBATIM. Do not refactor, reformat, rename, "optimize",
 * split, or convert it to @react-three/fiber. Every shader below is tuned; a
 * paraphrased shader is not a slightly-different scene, it is a black screen.
 *
 * Dependencies: `three` only.
 * Asset: `brain-mesh.bin` must be reachable at `brainMeshUrl` (default
 *        `/assets/brain/brain-mesh.bin`). It is fetched, never blocked on — if
 *        it 404s the orb and galaxy still run and the brain simply never draws.
 *
 * ── HOW THE LOOP WORKS ─────────────────────────────────────────────────────
 * The original site drives a 0→4 clock from 600vh of scroll. There is no scroll
 * here, so LOOP (below) drives the same clock from wall time instead: a table of
 * phases, each either holding the clock or ramping it with an eased curve. Every
 * scene term downstream is unchanged — it still just reads the clock.
 *
 * The cut back to the start is hidden inside the `burst` phase, which is the
 * site's own authored ending: the brain rushes the camera and scatters until
 * nothing is drawn. The clock resets during `black`, while the frame holds only
 * the ambient flame wash, so the loop has no visible seam.
 *
 * To retime the show, edit LOOP. Nothing else needs to change.
 * ========================================================================== */

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

/* ═══ the loop timetable — the only thing meant to be retuned ═════════════ */
const INTRO_DURATION_MS = 2900;

const LOOP = [
  { name: "intro",    dur: 2.9, from: 0,   to: 0   }, // orb assembles out of streaming points
  { name: "orb",      dur: 4.5, from: 0,   to: 0   }, // hold — it breathes, it answers the cursor
  { name: "toGalaxy", dur: 6.0, from: 0,   to: 1.6 }, // orb blows open, galaxy gathers, camera dives
  { name: "galaxy",   dur: 4.0, from: 1.6, to: 1.6 }, // hold — arms turn, disc tips edge-on
  { name: "toBrain",  dur: 6.5, from: 1.6, to: 3.4 }, // galaxy detonates, brain converges
  { name: "brain",    dur: 5.0, from: 3.4, to: 4.0 }, // hold — spins, synapses fire
  { name: "burst",    dur: 2.4, from: 4.0, to: 4.0 }, // brain rushes the camera and scatters
  { name: "black",    dur: 0.9, from: 4.0, to: 4.0 }, // ambient wash only; the clock resets here
];
const LOOP_TOTAL = LOOP.reduce((sum, p) => sum + p.dur, 0); // ≈ 32.2s

/* ═══ fixed parameters — do not retune ════════════════════════════════════ */
const ORB_CONFIG = {
  colorTop: "#52ffa5", colorBottom: "#582eff", colorEdge: "#582eff",
  deform: 0.135, brightness: 1.24, opacity: 1, spin: 0.17, tilt: 0.39,
  pointerRadius: 1.76, oilBulge: 0.46, oilRipple: 0.34, oilDrag: 0.95,
  rippleFreq: 11, rippleSpeed: 4, iridescence: 0.6,
  radius: 1, approach: 2.6, distortGain: 3.4,
};

const GALAXY_CONFIG = {
  colorEdge: "#52ffa5", colorCore: "#582eff",
  opacity: 0.55, pointSize: 6, brightness: 1.02, armSpin: 0.4,
  tilt: -0.5, scale: 0.18, cameraZ: 48, dive: 30, diveTilt: 0.5,
  parallax: 4, pointerRadius: 5, pointerStrength: 2,
};

const BRAIN_CONFIG = {
  colorCool: "#582eff", colorWarm: "#52ffa5", colorEdge: "#582eff",
  colorCenter: "#000000", colorSynapse: "#eafff8", colorDeep: "#02040e",
  colorCursor: "#6bfdff",
  centerRadius: 0.37, centerFalloff: 4, size: 0.067, synapseRate: 0.1,
  flowSpeed: 2.3, flowAmount: 0.025, glow: 1.4, depthDarkness: 1,
  radius: 1.15, cursorTilt: 0.22, scrollSpin: 2.4,
  baseRotationY: -1.309 - 1.5708, approach: 2.5,
};

/* Backdrop + mote palettes, crossfaded on galaxyPresence. */
const ORB_PALETTE = {
  bg: "#000000", flameA: "#8b98fe", flameB: "#6bfdff", flameAmt: 0.5,
  atmo: "#c9a8ff", atmoSpread: 2.2, atmoFadeNear: 2.0, atmoFadeFar: 3.2, atmoAlpha: 0.5,
};
const GALAXY_PALETTE = {
  bg: "#000000", flameA: "#8b98fe", flameB: "#6bfdff", flameAmt: 0.195,
  atmo: "#c9a8ff", atmoSpread: 4.0, atmoFadeNear: 5.0, atmoFadeFar: 6.5, atmoAlpha: 0.6,
};

const INTRO_DOLLY = 10;
const ORB_PARALLAX = 0.35;

/* Adaptive tiers, resolved once on mount — widest first, last match wins.
 * Point sprites are fill-rate bound (cost ∝ count × pointSize² × dpr²); these
 * numbers ARE the performance budget. Do not raise them. */
const TIERS = [
  { point: Infinity, orbCount: 23000, orbPointSize: 28, orbCameraZ: 3.8, orbMoveY: 0.8, galaxyW: 160, galaxyH: 420, brainCount: 130000, atmoCount: 260, atmoSize: 23, pointerReaction: true,  dpr: [1, 2],      targetFps: 60, bloom: true  },
  { point: 1440,     orbCount: 17000, orbPointSize: 27, orbCameraZ: 3.8, orbMoveY: 0.8, galaxyW: 130, galaxyH: 330, brainCount: 90000,  atmoCount: 210, atmoSize: 22, pointerReaction: true,  dpr: [1, 1.75],   targetFps: 60, bloom: true  },
  { point: 1024,     orbCount: 11000, orbPointSize: 24, orbCameraZ: 4.5, orbMoveY: 0.9, galaxyW: 100, galaxyH: 250, brainCount: 50000,  atmoCount: 150, atmoSize: 20, pointerReaction: false, dpr: [0.9, 1.25], targetFps: 45, bloom: true  },
  { point: 640,      orbCount: 6500,  orbPointSize: 21, orbCameraZ: 5.4, orbMoveY: 1.0, galaxyW: 70,  galaxyH: 170, brainCount: 26000,  atmoCount: 90,  atmoSize: 18, pointerReaction: false, dpr: [0.75, 1.1], targetFps: 30, bloom: false },
];

/* ═══ maths ══════════════════════════════════════════════════════════════ */
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const clamp01 = (x) => Math.min(Math.max(x, 0), 1);
const lerp = (a, b, t) => a + (b - a) * t;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const smoothstep = (a, b, x) => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };
const smootherstep = (x) => { const t = clamp01(x); return t * t * t * (t * (t * 6 - 15) + 10); };

/* THREE.Color converts sRGB → linear on construction. The renderer encodes
 * linear → sRGB on output, so shader colours MUST be linear or they get encoded
 * twice and the whole scene washes out. */
const linear = (hex) => { const c = new THREE.Color(hex); return new THREE.Vector3(c.r, c.g, c.b); };
const setLinear = (target, hex) => { const c = new THREE.Color(hex); target.set(c.r, c.g, c.b); };

/* ═══ the 0→4 timeline ═══════════════════════════════════════════════════ */
const KEYFRAMES = [
  { orbOut: 0, galaxyIn: 0, galaxyOut: 0,   galaxyDive: 0   },
  { orbOut: 1, galaxyIn: 1, galaxyOut: 0,   galaxyDive: 0.5 },
  { orbOut: 1, galaxyIn: 1, galaxyOut: 0.5, galaxyDive: 1   },
  { orbOut: 1, galaxyIn: 1, galaxyOut: 1,   galaxyDive: 1   },
  { orbOut: 1, galaxyIn: 1, galaxyOut: 1,   galaxyDive: 1   },
];
const KEYS = ["orbOut", "galaxyIn", "galaxyOut", "galaxyDive"];

const sampleScene = (g, out) => {
  const clamped = clamp(g, 0, 4);
  const lower = Math.min(Math.floor(clamped), KEYFRAMES.length - 2);
  const t = clamped - lower;
  const from = KEYFRAMES[lower];
  const to = KEYFRAMES[lower + 1];
  for (const key of KEYS) out[key] = lerp(from[key], to[key], t);
  return out;
};

const orbDissolve    = (s) => smootherstep(s.orbOut);
const orbAlpha       = (s) => 1 - orbDissolve(s);
const orbCoreOpen    = (g) => smoothstep(2.9, 3.6, g);
const orbApproach    = (g) => smoothstep(3.4, 4.0, g);
const galaxyAppear   = (s) => smootherstep(smoothstep(0.55, 0.95, s.galaxyIn));
const galaxyAssemble = (s) => 1 - smootherstep(smoothstep(0.5, 1.0, s.galaxyIn));
const galaxyBlow     = (s) => smootherstep(clamp01((s.galaxyOut - 0.5) * 2));
const galaxyPresence = (s) => galaxyAppear(s) * (1 - galaxyBlow(s));
const brainAppear    = (g) => smootherstep(smoothstep(2.6, 3.4, g));

/* Resolve the loop table at a cycle time, in seconds. */
const readLoop = (ct) => {
  let acc = 0;
  for (const phase of LOOP) {
    if (ct < acc + phase.dur || phase === LOOP[LOOP.length - 1]) {
      const local = clamp01((ct - acc) / phase.dur);
      // Ramps ease at both ends, matching the eased clock the site's keyframes
      // were authored against. Holds are unaffected (from === to).
      const progress = lerp(phase.from, phase.to, smootherstep(local));
      let intro = 1;
      let outro = 0;
      if (phase.name === "intro") intro = easeOutCubic(local);
      else if (phase.name === "burst") outro = local;
      else if (phase.name === "black") { intro = 0; outro = 1; }
      return { progress, intro, outro, phase: phase.name };
    }
    acc += phase.dur;
  }
  return { progress: 0, intro: 0, outro: 0, phase: "intro" };
};

/* ═══ shared GLSL ════════════════════════════════════════════════════════ */
const snoise = /* glsl */ `
  vec3 mod289(vec3 x){return x - floor(x*(1.0/289.0))*289.0;}
  vec4 mod289(vec4 x){return x - floor(x*(1.0/289.0))*289.0;}
  vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
  vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314*r;}
  float snoise(vec3 v){
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
        i.z + vec4(0.0, i1.z, i2.z, 1.0))
        + i.y + vec4(0.0, i1.y, i2.y, 1.0))
        + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }`;

const warp3d = /* glsl */ `
  vec3 warp3d(vec3 pos, float t){
    float curv = 0.8, a = 1.9, b = 0.7;
    pos *= 2.0;
    pos.x += curv*sin(t + a*pos.y) + t*b; pos.y += curv*cos(t + a*pos.x);
    pos.y += curv*sin(t + a*pos.z) + t*b; pos.z += curv*cos(t + a*pos.y);
    pos.z += curv*sin(t + a*pos.x) + t*b; pos.x += curv*cos(t + a*pos.z);
    return 0.5 + 0.5*cos(pos.xyz + vec3(1, 2, 4));
  }`;

const clipVert = /* glsl */ `
  varying vec2 vUv;
  void main(){ vUv = uv; gl_Position = vec4(position, 1.0); }`;

/* ═══ orb shaders ════════════════════════════════════════════════════════ */
const orbVertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uSize;
  uniform float uPR;
  uniform float uDeform;
  uniform float uOut;
  uniform float uAssemble;
  uniform float uCore;
  uniform vec3  uCentre;
  uniform vec3  uCamLocal;
  uniform vec3  uCursor;
  uniform vec3  uCursorVel;
  uniform float uEnergy;
  uniform float uPointerRadius;
  uniform float uOilBulge;
  uniform float uOilRipple;
  uniform float uOilDrag;
  uniform float uRippleFreq;
  uniform float uRippleSpeed;

  attribute vec3 aRandom;

  varying float vAlpha;
  varying float vLit;
  varying float vVert;
  varying float vSide;
  varying float vOil;

  ${snoise}

  void main(){
    vec3 p = position;

    // Soft radial deformation — two noise octaves, drifting over time.
    float n1 = snoise(p * 1.6 + vec3(0.0, uTime * 0.18, 0.0));
    float n2 = snoise(p * 3.3 - vec3(uTime * 0.12));
    float disp = n1 * 0.72 + n2 * 0.28;
    vec3 pos = p * (1.0 + uDeform * disp);

    float lit = smoothstep(-0.25, 0.5, disp);
    vLit = lit;

    // CORE: a bore through the blob along the view axis, in OBJECT space, so the
    // rim rides the displaced surface instead of being stamped over it.
    vec3 axis = normalize(uCamLocal);
    float axial = dot(pos, axis);
    float radial = length(pos - axis * axial);
    float bore = mix(0.36, 0.56, uCore) * (1.0 + disp * 0.55);
    float hole = smoothstep(bore, bore + 0.10, radial);

    // REVEAL: stream in from the camera, each point on its own delay.
    float delay = (aRandom.z + 0.5) * 0.55;
    float t = clamp((uAssemble - delay) / (1.0 - delay), 0.0, 1.0);
    float ease = 1.0 - pow(1.0 - t, 3.0);
    vec3 spawn = uCamLocal * 0.92
      + vec3(aRandom.x * 1.9, -1.7 + aRandom.y * 1.3, 0.0);
    pos = mix(spawn, pos, ease);
    float assembleFade = ease;

    // DISSOLVE: liquid sheets riding outward.
    float out2 = uOut * uOut;
    vec3 flow = normalize(p + aRandom * 0.4);
    float sheet = snoise(p * 1.9 + vec3(uTime * 0.25));
    float ripple = sin(length(p) * 6.0 - uTime * 1.6 + sheet * 3.0);
    float travel = out2 * (9.0 + sheet * 5.0 + ripple * 1.6);
    pos += flow * travel;
    pos += vec3(sheet, ripple, sheet * ripple) * uOut * 0.55;

    // OIL: viscous disturbance fixed in WORLD space (does not spin with the orb).
    vec4 wpos = modelMatrix * vec4(pos, 1.0);
    vec3 wN = normalize(wpos.xyz - uCentre);
    float ang = acos(clamp(dot(wN, normalize(uCursor)), -1.0, 1.0));
    float fall = smoothstep(uPointerRadius, 0.0, ang);
    float bulge = fall * fall;
    float oilRipple = sin(ang * uRippleFreq - uTime * uRippleSpeed) * fall;
    float oil = (bulge * uOilBulge + oilRipple * uOilRipple) * uEnergy;
    wpos.xyz += wN * oil;
    wpos.xyz += uCursorVel * fall * uOilDrag * uEnergy;
    vOil = (bulge + max(oilRipple, 0.0) * 0.5) * uEnergy;

    vec4 mv = viewMatrix * wpos;
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize * uPR * (1.0 / max(0.1, -mv.z));

    // View-space colouring — the gradient stays fixed to the screen.
    vVert = mv.y;
    vSide = abs(mv.x);

    float depth = pos.z * 0.5 + 0.5;
    float outFade = 1.0 - smoothstep(0.0, 0.92, uOut);

    // Alpha floors: additive points can only ADD light, but a point whose alpha
    // collapses reveals the near-black backdrop and reads as a black blotch.
    vAlpha = hole * (0.28 + 0.32 * depth) * (0.38 + 0.72 * lit) * outFade * assembleFade;
  }`;

const orbFragmentShader = /* glsl */ `
  precision highp float;

  uniform vec3  uColTop;
  uniform vec3  uColBottom;
  uniform vec3  uColEdge;
  uniform float uBrightness;
  uniform float uOpacity;
  uniform float uAppear;
  uniform float uIri;

  varying float vAlpha;
  varying float vLit;
  varying float vVert;
  varying float vSide;
  varying float vOil;

  void main(){
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    if (d > 0.5) discard;
    // Tight bright core + thin halo — wide soft blobs overlapping read "soapy".
    float soft = smoothstep(0.5, 0.0, d);
    soft = soft * soft * 1.2;
    float core = smoothstep(0.13, 0.0, d);

    float vt = clamp(vVert * 0.5 + 0.5, 0.0, 1.0);
    vec3 col = mix(uColBottom, uColTop, vt);
    col = mix(col, uColEdge, smoothstep(0.72, 1.15, vSide));
    col *= (0.78 + 0.42 * vLit);
    col += smoothstep(0.6, 1.0, vLit) * core * vec3(0.45);

    float cap = smoothstep(0.55, 1.05, abs(vVert));
    col += col * cap * 1.1;
    float capA = 1.0 + 0.6 * cap;

    // Oil slick: thin-film iridescence over the disturbed area.
    float oilMask = clamp(vOil, 0.0, 1.0);
    vec3 iri = 0.5 + 0.5 * cos(6.2831853 * (vOil * 2.4 + abs(vVert) * 0.3 + vec3(0.0, 0.33, 0.67)));
    col = mix(col, iri, oilMask * uIri);
    col += oilMask * 0.3;

    col *= uBrightness;
    gl_FragColor = vec4(col, soft * vAlpha * capA * uOpacity * uAppear * (1.0 + oilMask * 0.5));
  }`;

/* ═══ galaxy shaders ═════════════════════════════════════════════════════ */
const galaxyVertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uSize;
  uniform float uArmSpin;
  uniform float uScale;
  uniform float uBlow;
  uniform float uAssemble;
  uniform vec3  uColEdge;
  uniform vec3  uColCore;
  uniform vec3  uCursor;
  uniform float uRepelRadius;
  uniform float uRepelStrength;
  uniform float uActivity;

  varying float vFade;
  varying vec3  vColor;

  void main() {
    float gRnd1 = fract(sin(dot(position.xyz, vec3(12.989, 78.233, 45.164))) * 43758.545);
    float gRnd2 = fract(sin(dot(position.xyz, vec3(93.989, 67.345, 54.256))) * 24634.634);
    float gRnd3 = fract(sin(dot(position.xyz, vec3(43.332, 11.235, 89.234))) * 56475.234);
    float gRnd4 = fract(sin(dot(position.xyz, vec3(75.321, 32.123, 23.456))) * 35432.123);

    float galaxyR = pow(gRnd1, 2.0) * 60.0 + pow(gRnd2, 3.0) * 30.0;
    float swirl = pow(galaxyR, 1.1) * 0.08;
    float armIndex = floor(gRnd2 * 2.0);
    float baseAngle = armIndex * 3.14159265;
    float uniformTheta = gRnd3 * 2.0 - 1.0;
    float dTheta = pow(uniformTheta, 5.0) * 3.14159;
    float theta = baseAngle + swirl + dTheta + uTime * uArmSpin;

    float gx = galaxyR * cos(theta);
    float gz = galaxyR * sin(theta);
    float gyDisc = pow(gRnd4 * 2.0 - 1.0, 3.0) * 1.5;
    vec3 basePos = vec3(gx, gyDisc, gz);

    float bulgeStrength = smoothstep(25.0, 0.0, galaxyR);
    float gRnd5 = fract(sin(gRnd1 * 44.44 + gRnd2) * 555.55);
    float gRnd6 = fract(sin(gRnd3 * 66.66 + gRnd4) * 777.77);
    float gRnd7 = fract(sin(gRnd5 * 88.88 + gRnd6) * 999.99);
    float phi = acos(gRnd5 * 2.0 - 1.0);
    float thetaS = gRnd6 * 6.2831853 + uTime * (uArmSpin * 2.0);
    float rS = pow(gRnd7, 2.0) * 18.0;
    vec3 bulge = vec3(rS * sin(phi) * cos(thetaS), rS * cos(phi), rS * sin(phi) * sin(thetaS));

    vec3 galaxyPos = mix(basePos, bulge, bulgeStrength);

    vec3 blowDir = normalize(vec3(gRnd1, gRnd2, gRnd3) - 0.5 + 0.0001);

    // Assembly — each star streams in from far out along its own vector, on its
    // own delay, so the disc gathers out of drifting dust instead of fading on.
    float delay = gRnd4 * 0.45;
    float at = clamp((1.0 - uAssemble - delay) / (1.0 - delay), 0.0, 1.0);
    float aEase = 1.0 - pow(1.0 - at, 3.0);
    vec3 spawn = galaxyPos + blowDir * 70.0 + vec3(0.0, (gRnd3 - 0.5) * 26.0, 0.0);
    galaxyPos = mix(spawn, galaxyPos, aEase);

    // Dispersal — every point flies out along its own direction.
    galaxyPos += blowDir * uBlow * 90.0;

    vec3 finalPos = galaxyPos * uScale;
    vec4 modelPosition = modelMatrix * vec4(finalPos, 1.0);

    vec3 toP = modelPosition.xyz - uCursor;
    float cd = length(toP);
    float fall = smoothstep(uRepelRadius, 0.0, cd);
    modelPosition.xyz += normalize(toP + vec3(0.0001)) * fall * uRepelStrength * uActivity;
    vec4 mvPosition = viewMatrix * modelPosition;

    float coreMix = smoothstep(80.0, 0.0, galaxyR);
    vColor = mix(uColEdge, uColCore, clamp(coreMix, 0.0, 1.0));

    float isOrb = step(0.98, fract(gRnd1 * 77.77));
    float starSize = mix(1.0, 3.0, isOrb);
    vFade = mix(0.7, 1.0, isOrb);

    gl_PointSize = uSize * starSize * (10.0 / -mvPosition.z);
    gl_PointSize = max(gl_PointSize, 1.5);
    gl_Position = projectionMatrix * mvPosition;
  }`;

const galaxyFragmentShader = /* glsl */ `
  uniform float uOpacity;
  uniform float uBrightness;
  uniform float uAppear;
  uniform float uFade;

  varying float vFade;
  varying vec3  vColor;

  void main() {
    vec2 xy = gl_PointCoord - 0.5;
    float ll = length(xy);
    if (ll > 0.5) discard;
    float a = smoothstep(0.5, 0.1, ll);
    gl_FragColor = vec4(vColor * uBrightness, vFade * a * uOpacity * uAppear * uFade);
  }`;

/* ═══ brain shaders ══════════════════════════════════════════════════════ */
const brainVertexShader = /* glsl */ `
  attribute float aSeed; attribute float aOcclusion; attribute vec3 aNormal;
  uniform float iTime; uniform float iResolutionY; uniform float uSize; uniform float uSynapseRate;
  uniform float uCenterRadius; uniform float uFlowSpeed; uniform float uFlowAmount;
  uniform vec3 uHighlightPos; uniform float uHighlightRadius; uniform float uHighlightStrength;
  uniform float uExplode; uniform float uExplodeDist;
  uniform vec2 uMouse; uniform float uCursor; uniform float uAspect; uniform float uCursorRadius;
  varying float vSeed; varying float vSynapse; varying float vHemi; varying float vDepth;
  varying float vFrontness; varying float vCenterness; varying float vOcclusion; varying float vHighlight;
  varying float vFar; varying float vCursor; varying vec3 vWorldPos;
  void main() {
    vSeed = aSeed; vOcclusion = aOcclusion;
    vec3 p = position; vWorldPos = p; vHemi = step(0.0, p.x);
    vHighlight = (1.0 - smoothstep(0.0, uHighlightRadius, distance(position, uHighlightPos))) * uHighlightStrength;
    vec3 focalDir = normalize(uHighlightPos + vec3(1e-5));
    float align = dot(normalize(position + vec3(1e-5)), focalDir);
    vFar = smoothstep(0.55, -0.35, align);
    vec3 rad = normalize(p + vec3(1e-5));
    float breathe = sin(iTime * 1.6 + aSeed * 6.0) * 0.012;
    p += rad * breathe;
    vec3 nrm = normalize(aNormal + vec3(1e-5));
    vec3 ref = abs(nrm.y) < 0.95 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 tA = normalize(cross(nrm, ref));
    vec3 tB = cross(nrm, tA);
    float ph = iTime * uFlowSpeed + aSeed * 6.2831;
    vec3 loopDir = tA * cos(ph) + tB * sin(ph);
    p += loopDir * uFlowAmount;
    vec3 exDir = normalize(rad + vec3(sin(aSeed * 41.0), cos(aSeed * 57.0), sin(aSeed * 73.0)) * 0.45);
    p += exDir * uExplode * uExplodeDist;
    float period = mix(3.0, 9.0, aSeed);
    float firePhase = aSeed * period;
    float ft = mod(iTime + firePhase, period);
    float fire = pow(clamp(1.0 - ft / 0.4, 0.0, 1.0), 2.5);
    if (aSeed > uSynapseRate) fire = 0.0;
    vSynapse = fire;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vec4 centerMv = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    float rel = centerMv.z - mv.z;
    vFrontness = clamp(rel * 0.6 + 0.5, 0.0, 1.0);
    gl_Position = projectionMatrix * mv;
    vec4 centerClip = projectionMatrix * centerMv;
    vec2 centerNDC = centerClip.xy / max(0.0001, centerClip.w);
    vec2 pNDC = gl_Position.xy / max(0.0001, gl_Position.w);
    float screenDist = length(pNDC - centerNDC);
    vCenterness = 1.0 - clamp(screenDist / max(0.05, uCenterRadius), 0.0, 1.0);
    vec2 dMouse = pNDC - uMouse; dMouse.x *= uAspect;
    vCursor = (1.0 - smoothstep(0.0, uCursorRadius, length(dMouse))) * uCursor;
    float baseSize = uSize * (iResolutionY / 720.0) * (200.0 / -mv.z);
    gl_PointSize = baseSize * (1.0 + fire * 0.8 + vHighlight * 1.8 + vCursor * 1.3);
    vDepth = -mv.z;
  }`;

const brainFragmentShader = /* glsl */ `
  uniform vec3 uCool; uniform vec3 uWarm; uniform vec3 uEdgeColor; uniform vec3 uCenterColor;
  uniform float uCenterFalloff; uniform vec3 uSynapse; uniform float iAlpha; uniform float uGlow;
  uniform float uDepthDarkness; uniform vec3 uDeepColor; uniform float uOcclusionStrength;
  uniform vec3 uHighlightColor; uniform float uHighlightStrength; uniform float uFocusFadeStrength;
  uniform float uIsolateStrength; uniform float uExplode; uniform vec3 uCursorColor;
  varying float vSeed; varying float vSynapse; varying float vHemi; varying float vDepth;
  varying float vFrontness; varying float vCenterness; varying float vOcclusion; varying float vHighlight;
  varying float vFar; varying float vCursor; varying vec3 vWorldPos;
  void main() {
    vec2 p = gl_PointCoord - 0.5;
    float r = length(p);
    if (r > 0.5) discard;
    float core = pow(smoothstep(0.5, 0.0, r), 2.2);
    float t = pow(vCenterness, max(0.05, uCenterFalloff));
    float vt = clamp(smoothstep(-0.7, 0.9, vWorldPos.y) + vSeed * 0.12, 0.0, 1.0);
    vec3 grad = mix(uCool, uWarm, vt);
    vec3 base = mix(grad, uCenterColor, t);
    base = mix(base, uEdgeColor, (1.0 - t) * 0.18);
    base = mix(base, uDeepColor, clamp(vOcclusion * uOcclusionStrength, 0.0, 1.0));
    vec3 col = base + uSynapse * vSynapse * 2.0;
    col = mix(col, uHighlightColor, vHighlight * 0.5);
    col += uHighlightColor * vHighlight * 0.7;
    float nonFocus = (1.0 - vHighlight) * uHighlightStrength;
    col = mix(col, uDeepColor, nonFocus * uIsolateStrength);
    float depthMul = mix(1.0 - uDepthDarkness, 1.0, vFrontness);
    col *= depthMul;
    float alphaOut = core * iAlpha * mix(1.0 - uDepthDarkness * 0.7, 1.0, vFrontness);
    alphaOut *= 1.0 + vHighlight * 0.8;
    float focusDim = 1.0 - uHighlightStrength * uFocusFadeStrength * vFar;
    col *= focusDim; alphaOut *= focusDim;
    col += uCursorColor * vCursor * 0.8;
    alphaOut += vCursor * core * 0.32;
    alphaOut *= 1.0 - smoothstep(0.0, 1.0, uExplode) * 0.8;
    gl_FragColor = vec4(col * uGlow, alphaOut);
  }`;

/* ═══ brain mesh: decode + area-weighted surface sample ══════════════════ */
const BRAIN_MAGIC = 0x4e524256; // 'VBRN', little-endian
const BRAIN_HEADER_BYTES = 16;
const BRAIN_QUANT = 32767;

const decodeBrainMesh = (buffer) => {
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== BRAIN_MAGIC) {
    throw new Error("brain mesh: bad magic — not a VBRN container");
  }
  const version = view.getUint32(4, true);
  if (version !== 1) throw new Error(`brain mesh: unsupported version ${version}`);
  const vertexCount = view.getUint32(8, true);
  const indexCount = view.getUint32(12, true);
  const quantised = new Int16Array(buffer, BRAIN_HEADER_BYTES, vertexCount * 3);
  const positions = new Float32Array(vertexCount * 3);
  for (let i = 0; i < positions.length; i++) positions[i] = quantised[i] / BRAIN_QUANT;
  const indexBase = BRAIN_HEADER_BYTES + vertexCount * 3 * 2;
  const indices = new Uint16Array(buffer.slice(indexBase, indexBase + indexCount * 2));
  return { positions, indices };
};

const buildBrainGeometry = (mesh, count, radius) => {
  const verts = mesh.positions;
  const indices = mesh.indices;
  const triangleCount = indices.length / 3;

  // Cumulative area, so a uniform draw picks a triangle in proportion to the
  // surface it owns — otherwise dense clusters of tiny triangles get
  // oversampled and the cloud clumps.
  const cdf = new Float32Array(triangleCount);
  let total = 0;
  for (let t = 0; t < triangleCount; t++) {
    const a = indices[t * 3] * 3;
    const b = indices[t * 3 + 1] * 3;
    const c = indices[t * 3 + 2] * 3;
    const ux = verts[b] - verts[a];
    const uy = verts[b + 1] - verts[a + 1];
    const uz = verts[b + 2] - verts[a + 2];
    const vx = verts[c] - verts[a];
    const vy = verts[c + 1] - verts[a + 1];
    const vz = verts[c + 2] - verts[a + 2];
    total += Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx) * 0.5;
    cdf[t] = total;
  }
  for (let t = 0; t < triangleCount; t++) cdf[t] /= total;

  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const seeds = new Float32Array(count);
  // The shader reads `aOcclusion`, but the model carries none — all zeros, and
  // `uOcclusionStrength` is 0 to match. Kept so the attribute layout is intact.
  const occlusion = new Float32Array(count);

  for (let s = 0; s < count; s++) {
    const pick = Math.random();
    let lo = 0;
    let hi = triangleCount - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cdf[mid] < pick) lo = mid + 1;
      else hi = mid;
    }

    const a = indices[lo * 3] * 3;
    const b = indices[lo * 3 + 1] * 3;
    const c = indices[lo * 3 + 2] * 3;

    // Uniform barycentric point: fold the far half of the unit square back over
    // the diagonal, so the pair lands inside the triangle rather than the quad.
    let u = Math.random();
    let v = Math.random();
    if (u + v > 1) { u = 1 - u; v = 1 - v; }
    const w = 1 - u - v;

    positions[s * 3]     = (w * verts[a]     + u * verts[b]     + v * verts[c])     * radius;
    positions[s * 3 + 1] = (w * verts[a + 1] + u * verts[b + 1] + v * verts[c + 1]) * radius;
    positions[s * 3 + 2] = (w * verts[a + 2] + u * verts[b + 2] + v * verts[c + 2]) * radius;

    const e1x = verts[b] - verts[a];
    const e1y = verts[b + 1] - verts[a + 1];
    const e1z = verts[b + 2] - verts[a + 2];
    const e2x = verts[c] - verts[a];
    const e2y = verts[c + 1] - verts[a + 1];
    const e2z = verts[c + 2] - verts[a + 2];
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    const length = Math.hypot(nx, ny, nz) || 1;
    normals[s * 3] = nx / length;
    normals[s * 3 + 1] = ny / length;
    normals[s * 3 + 2] = nz / length;

    seeds[s] = Math.random();
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("aNormal", new THREE.BufferAttribute(normals, 3));
  geometry.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
  geometry.setAttribute("aOcclusion", new THREE.BufferAttribute(occlusion, 1));
  return geometry;
};

/* ═══ the viscous cursor pointer ═════════════════════════════════════════ */
const createOilPointer = (radius) => {
  const smooth = new THREE.Vector3(0, 0, radius);
  const prev = new THREE.Vector3(0, 0, radius);
  const velocity = new THREE.Vector3();
  const rawVel = new THREE.Vector3();
  const ndc = new THREE.Vector3();
  const dir = new THREE.Vector3();
  const originToCentre = new THREE.Vector3();
  const target = new THREE.Vector3();
  const radial = new THREE.Vector3();
  const pointer = new THREE.Vector2();
  const state = { energy: 0, idle: 0 };

  return {
    cursor: smooth,
    velocity,
    get energy() { return state.energy; },
    step(camera, pointerNdc, centre, enabled, delta) {
      // Frame-rate-independent easing: `base` is the fraction closed per 1/60 s.
      const ease = (base) => 1 - Math.pow(1 - base, delta * 60);
      pointer.lerp(pointerNdc, ease(0.11));

      target.copy(smooth);
      if (enabled) {
        ndc.set(pointer.x, pointer.y, 0.5).unproject(camera);
        dir.copy(ndc).sub(camera.position).normalize();
        originToCentre.copy(camera.position).sub(centre);
        const b = 2 * dir.dot(originToCentre);
        const c = originToCentre.lengthSq() - radius * radius;
        const disc = b * b - 4 * c;
        if (disc >= 0) {
          const t = (-b - Math.sqrt(disc)) / 2;
          target.copy(originToCentre).addScaledVector(dir, t);
        } else {
          const tc = Math.max(0, -dir.dot(originToCentre));
          target.copy(originToCentre).addScaledVector(dir, tc).normalize().multiplyScalar(radius);
        }
      }

      prev.copy(smooth);
      smooth.lerp(target, ease(0.1)); // viscous lag

      rawVel.subVectors(smooth, prev).multiplyScalar(2.5);
      radial.copy(smooth).normalize();
      rawVel.addScaledVector(radial, -rawVel.dot(radial)); // tangential smear only
      if (rawVel.length() > 0.6) rawVel.setLength(0.6);
      velocity.lerp(rawVel, ease(0.16)); // low-pass, or the bulge twitches

      const speed = smooth.distanceTo(prev);
      const drive = Math.min(Math.max(speed * 8, 0), 1);
      if (drive > state.energy) state.energy += (drive - state.energy) * ease(0.08);
      else state.energy *= Math.pow(0.97, delta * 60);

      state.idle = speed < 0.0005 ? state.idle + delta : 0;
      if (!enabled || state.idle > 2.5) state.energy *= Math.pow(0.9, delta * 60);
    },
  };
};

/* ═══ component ══════════════════════════════════════════════════════════ */
export default function VesperSequence({
  brainMeshUrl = "/assets/brain/brain-mesh.bin",
  /** "full" = the site's own tiers. "balanced" drops one tier for a lighter
   *  scene at the same composition — sensible on a page people visit daily. */
  quality = "full",
  /** Where the forms sit across the frame, 0 = hard left, 1 = hard right.
   *  0.32 clears a right-hand content column. Relaxes to 0.5 below 1024px,
   *  where the content stacks and there is no column to clear. */
  focusX = 0.32,
}) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let tierIndex = 0;
    TIERS.forEach((t, i) => { if (window.innerWidth <= t.point) tierIndex = i; });
    if (quality === "balanced") tierIndex = Math.min(tierIndex + 1, TIERS.length - 1);
    const params = TIERS[tierIndex];

    /* renderer */
    const renderer = new THREE.WebGLRenderer({
      alpha: true, antialias: false, powerPreference: "high-performance",
    });
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, params.dpr[0]), params.dpr[1]);
    renderer.setPixelRatio(dpr);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(ORB_PALETTE.bg);

    const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 400);
    camera.position.set(0, 0, params.orbCameraZ + INTRO_DOLLY);

    /* bloom. Threshold is in LINEAR light — a low value catches the background
     * wash and smears the frame. OutputPass does the tonemap + sRGB encode the
     * composer's render targets skip; without it the scene renders dark. */
    let composer = null;
    if (params.bloom) {
      composer = new EffectComposer(renderer);
      composer.setPixelRatio(dpr);
      composer.setSize(window.innerWidth, window.innerHeight);
      composer.addPass(new RenderPass(scene, camera));
      composer.addPass(new UnrealBloomPass(
        new THREE.Vector2(window.innerWidth, window.innerHeight), 0.6, 0.3, 0.6,
      ));
      composer.addPass(new OutputPass());
    }

    const disposables = [];

    /* ── 1. backdrop — full-screen flame wash ───────────────────────────── */
    const orbVecs = { bg: linear(ORB_PALETTE.bg), flameA: linear(ORB_PALETTE.flameA), flameB: linear(ORB_PALETTE.flameB) };
    const galVecs = { bg: linear(GALAXY_PALETTE.bg), flameA: linear(GALAXY_PALETTE.flameA), flameB: linear(GALAXY_PALETTE.flameB) };

    const backdropUniforms = {
      iTime: { value: 0 },
      uBg: { value: orbVecs.bg.clone() },
      uFlameA: { value: orbVecs.flameA.clone() },
      uFlameB: { value: orbVecs.flameB.clone() },
      uFlameAmt: { value: ORB_PALETTE.flameAmt },
    };
    const backdropGeo = new THREE.PlaneGeometry(2, 2);
    const backdropMat = new THREE.ShaderMaterial({
      uniforms: backdropUniforms,
      vertexShader: clipVert,
      fragmentShader: /* glsl */ `
        uniform float iTime;
        uniform vec3  uBg;
        uniform vec3  uFlameA;
        uniform vec3  uFlameB;
        uniform float uFlameAmt;
        varying vec2 vUv;
        ${warp3d}
        void main(){
          vec2 uv = 2.0 * vUv - 1.0;
          vec3 w = pow(warp3d(vec3(uv.x, sin(uv.y), uv.y), iTime * 1.5), vec3(1.5));
          vec3 flame = 1.5 * uFlameA * w.x;
          flame *= w.y;
          flame += uFlameB * w.z;
          flame *= smoothstep(0.25, 1.0, abs(uv.y));
          float md = smoothstep(-0.7, 1.0, -uv.y * uv.x);
          flame *= md * md;
          vec3 bg = uBg * (1.0 - 0.4 * length(uv));
          gl_FragColor = vec4(bg + flame * uFlameAmt, 1.0);
        }`,
      depthTest: false, depthWrite: false, toneMapped: false,
    });
    const backdrop = new THREE.Mesh(backdropGeo, backdropMat);
    backdrop.frustumCulled = false;
    backdrop.renderOrder = -1;
    scene.add(backdrop);
    disposables.push(backdropGeo, backdropMat);

    /* ── 2. the orb ─────────────────────────────────────────────────────── */
    const buildOrbGeometry = (count, radius) => {
      const positions = new Float32Array(count * 3);
      const randoms = new Float32Array(count * 3);
      const golden = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2;
        const r = Math.sqrt(Math.max(0, 1 - y * y));
        const th = golden * i;
        positions[i * 3] = Math.cos(th) * r * radius;
        positions[i * 3 + 1] = y * radius;
        positions[i * 3 + 2] = Math.sin(th) * r * radius;
        randoms[i * 3] = Math.random() - 0.5;
        randoms[i * 3 + 1] = Math.random() - 0.5;
        randoms[i * 3 + 2] = Math.random() - 0.5;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      g.setAttribute("aRandom", new THREE.BufferAttribute(randoms, 3));
      return g;
    };

    const orbUniforms = {
      uTime: { value: 0 }, uPR: { value: 1 }, uSize: { value: params.orbPointSize },
      uDeform: { value: ORB_CONFIG.deform }, uOut: { value: 0 },
      uAssemble: { value: 0 }, uCore: { value: 0 },
      uCentre: { value: new THREE.Vector3() },
      uCamLocal: { value: new THREE.Vector3(0, 0, 1) },
      uColTop: { value: linear(ORB_CONFIG.colorTop) },
      uColBottom: { value: linear(ORB_CONFIG.colorBottom) },
      uColEdge: { value: linear(ORB_CONFIG.colorEdge) },
      uBrightness: { value: ORB_CONFIG.brightness },
      uOpacity: { value: ORB_CONFIG.opacity },
      uAppear: { value: 1 },
      uCursor: { value: new THREE.Vector3(0, 0, ORB_CONFIG.radius) },
      uCursorVel: { value: new THREE.Vector3() },
      uEnergy: { value: 0 },
      uPointerRadius: { value: ORB_CONFIG.pointerRadius },
      uOilBulge: { value: ORB_CONFIG.oilBulge },
      uOilRipple: { value: ORB_CONFIG.oilRipple },
      uOilDrag: { value: ORB_CONFIG.oilDrag },
      uRippleFreq: { value: ORB_CONFIG.rippleFreq },
      uRippleSpeed: { value: ORB_CONFIG.rippleSpeed },
      uIri: { value: ORB_CONFIG.iridescence },
    };
    const orbGeo = buildOrbGeometry(params.orbCount, ORB_CONFIG.radius);
    const orbMat = new THREE.ShaderMaterial({
      uniforms: orbUniforms,
      vertexShader: orbVertexShader,
      fragmentShader: orbFragmentShader,
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    const orbGroup = new THREE.Group();
    const orbPoints = new THREE.Points(orbGeo, orbMat);
    orbPoints.frustumCulled = false;
    orbGroup.add(orbPoints);
    orbGroup.visible = false;
    scene.add(orbGroup);
    disposables.push(orbGeo, orbMat);

    const oil = createOilPointer(ORB_CONFIG.radius);
    const orbCentre = new THREE.Vector3();
    const camLocal = new THREE.Vector3();

    /* ── 3. the galaxy ──────────────────────────────────────────────────── */
    /* SphereGeometry is only a seed lattice — the vertex shader hashes each
     * position into an arm or bulge coordinate. It is indexed, and THREE.Points
     * honours the index, so every vertex would draw ~6× stacked exactly on top
     * of itself. Dropping the index draws each star once; additive blending is
     * linear, so scaling opacity by the duplicate factor reproduces the same
     * image at a sixth of the work. */
    const galaxySphere = new THREE.SphereGeometry(4.2, params.galaxyW, params.galaxyH);
    const galaxyVertices = galaxySphere.attributes.position.count;
    const galaxyDuplicates = galaxySphere.index ? galaxySphere.index.count / galaxyVertices : 1;
    galaxySphere.setIndex(null);
    const galaxyOpacity = GALAXY_CONFIG.opacity * galaxyDuplicates;

    const galaxyUniforms = {
      uTime: { value: 0 }, uAppear: { value: 0 }, uFade: { value: 1 },
      uBlow: { value: 0 }, uAssemble: { value: 1 },
      uColEdge: { value: linear(GALAXY_CONFIG.colorEdge) },
      uColCore: { value: linear(GALAXY_CONFIG.colorCore) },
      uOpacity: { value: galaxyOpacity },
      uSize: { value: GALAXY_CONFIG.pointSize },
      uBrightness: { value: GALAXY_CONFIG.brightness },
      uArmSpin: { value: GALAXY_CONFIG.armSpin },
      uScale: { value: GALAXY_CONFIG.scale },
      uCursor: { value: new THREE.Vector3() },
      uRepelRadius: { value: GALAXY_CONFIG.pointerRadius },
      uRepelStrength: { value: GALAXY_CONFIG.pointerStrength },
      uActivity: { value: 0 },
    };
    const galaxyMat = new THREE.ShaderMaterial({
      uniforms: galaxyUniforms,
      vertexShader: galaxyVertexShader,
      fragmentShader: galaxyFragmentShader,
      transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const galaxyGroup = new THREE.Group();
    const galaxyPoints = new THREE.Points(galaxySphere, galaxyMat);
    galaxyPoints.frustumCulled = false;
    galaxyGroup.add(galaxyPoints);
    galaxyGroup.visible = false;
    scene.add(galaxyGroup);
    disposables.push(galaxySphere, galaxyMat);

    const galaxyCursor = new THREE.Vector3();
    const galaxyCursorTarget = new THREE.Vector3();
    const galaxyNdc = new THREE.Vector3();
    const galaxyRay = new THREE.Vector3();
    let galaxyActivity = 0;

    /* ── 4. the brain (fetched, never blocked on) ───────────────────────── */
    const brainUniforms = {
      iTime: { value: 0 }, iAlpha: { value: 0 },
      iResolutionY: { value: window.innerHeight * dpr },
      uCool: { value: linear(BRAIN_CONFIG.colorCool) },
      uWarm: { value: linear(BRAIN_CONFIG.colorWarm) },
      uEdgeColor: { value: linear(BRAIN_CONFIG.colorEdge) },
      uCenterColor: { value: linear(BRAIN_CONFIG.colorCenter) },
      uSynapse: { value: linear(BRAIN_CONFIG.colorSynapse) },
      uDeepColor: { value: linear(BRAIN_CONFIG.colorDeep) },
      uCursorColor: { value: linear(BRAIN_CONFIG.colorCursor) },
      uHighlightColor: { value: linear("#2563eb") },
      uCenterRadius: { value: BRAIN_CONFIG.centerRadius },
      uCenterFalloff: { value: BRAIN_CONFIG.centerFalloff },
      uSize: { value: BRAIN_CONFIG.size },
      uSynapseRate: { value: BRAIN_CONFIG.synapseRate },
      uFlowSpeed: { value: BRAIN_CONFIG.flowSpeed },
      uFlowAmount: { value: BRAIN_CONFIG.flowAmount },
      uGlow: { value: BRAIN_CONFIG.glow },
      uDepthDarkness: { value: BRAIN_CONFIG.depthDarkness },
      uOcclusionStrength: { value: 0 },
      uHighlightPos: { value: new THREE.Vector3(0, 0, 0) },
      uHighlightRadius: { value: 0.6 },
      uHighlightStrength: { value: 0 },
      uFocusFadeStrength: { value: 0.55 },
      uIsolateStrength: { value: 0.88 },
      uExplode: { value: 1 },
      uExplodeDist: { value: 5 },
      uMouse: { value: new THREE.Vector2(-10, -10) },
      uCursor: { value: 0 },
      uAspect: { value: 1 },
      uCursorRadius: { value: 0.1 },
    };
    const brainMat = new THREE.ShaderMaterial({
      uniforms: brainUniforms,
      vertexShader: brainVertexShader,
      fragmentShader: brainFragmentShader,
      transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const brainGroup = new THREE.Group();
    brainGroup.visible = false;
    scene.add(brainGroup);
    disposables.push(brainMat);

    let brainGeo = null;
    let brainPoints = null;
    const brainParallax = { ry: 0, rx: 0 };
    const abort = new AbortController();

    fetch(brainMeshUrl, { signal: abort.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`brain mesh: ${r.status} ${r.statusText}`);
        return r.arrayBuffer();
      })
      .then((buffer) => {
        if (abort.signal.aborted) return;
        brainGeo = buildBrainGeometry(decodeBrainMesh(buffer), params.brainCount, BRAIN_CONFIG.radius);
        brainPoints = new THREE.Points(brainGeo, brainMat);
        brainPoints.frustumCulled = false;
        brainGroup.add(brainPoints);
      })
      .catch((error) => {
        if (abort.signal.aborted) return;
        // A missing brain must not take the rest of the scene down with it.
        console.error("[vesper] brain mesh failed to load:", error);
      });

    /* ── 5. atmosphere — camera-attached motes ──────────────────────────── */
    const atmoPositions = new Float32Array(params.atmoCount * 3);
    const atmoSizes = new Float32Array(params.atmoCount);
    for (let i = 0; i < params.atmoCount; i++) {
      atmoPositions[i * 3] = 2 * Math.random() - 1;
      atmoPositions[i * 3 + 1] = 2 * Math.random() - 1;
      atmoPositions[i * 3 + 2] = 2 * Math.random() - 1;
      atmoSizes[i] = params.atmoSize * (0.4 + Math.random());
    }
    const atmoGeo = new THREE.BufferGeometry();
    atmoGeo.setAttribute("position", new THREE.BufferAttribute(atmoPositions, 3));
    atmoGeo.setAttribute("size", new THREE.BufferAttribute(atmoSizes, 1));

    const atmoOrbColor = linear(ORB_PALETTE.atmo);
    const atmoGalaxyColor = linear(GALAXY_PALETTE.atmo);
    const atmoUniforms = {
      uTime: { value: 0 },
      uColor: { value: atmoOrbColor.clone() },
      uAlpha: { value: ORB_PALETTE.atmoAlpha },
      uSpread: { value: ORB_PALETTE.atmoSpread },
      uFadeNear: { value: ORB_PALETTE.atmoFadeNear },
      uFadeFar: { value: ORB_PALETTE.atmoFadeFar },
      uRes: { value: new THREE.Vector2(1, 1) },
    };
    const atmoMat = new THREE.ShaderMaterial({
      uniforms: atmoUniforms,
      vertexShader: /* glsl */ `
        attribute float size;
        uniform float uTime;
        uniform vec2  uRes;
        uniform float uSpread;
        uniform float uFadeNear;
        uniform float uFadeFar;
        varying float vA;
        vec3 warp(vec3 p, float t){
          float c = 0.9, a = 1.9, b = 0.02, s = 0.05;
          p *= 2.0;
          p.x += c*sin(s*t + a*p.y) + t*b; p.y += c*cos(s*t + a*p.x);
          p.y += c*sin(s*t + a*p.z) + t*b; p.z += c*cos(s*t + a*p.y);
          p.z += c*sin(s*t + a*p.x) + t*b; p.x += c*cos(s*t + a*p.z);
          return cos(p + vec3(1, 2, 4));
        }
        void main(){
          vec3 v = position * uSpread + warp(position, uTime) * (uSpread * 0.28);
          vec4 mv = modelViewMatrix * vec4(v, 1.0);
          float r = length(v);
          float farF = 1.0 - smoothstep(uFadeNear, uFadeFar, r);
          float nearF = smoothstep(0.0, 0.3, -mv.z);
          vA = farF * nearF;
          gl_PointSize = size * uRes.y / 900.0 / -mv.z;
          gl_PointSize = max(gl_PointSize, 1.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3  uColor;
        uniform float uAlpha;
        varying float vA;
        void main(){
          vec2 p = gl_PointCoord - 0.5;
          float l = length(p);
          if (l > 0.5) discard;
          float tex = smoothstep(0.5, 0.0, l);
          gl_FragColor = vec4(uColor * tex, tex * vA * uAlpha);
        }`,
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    const atmosphere = new THREE.Points(atmoGeo, atmoMat);
    atmosphere.frustumCulled = false;
    scene.add(atmosphere);
    disposables.push(atmoGeo, atmoMat);

    /* ── pointer ────────────────────────────────────────────────────────── */
    const pointer = new THREE.Vector2(0, 0);
    const onPointerMove = (e) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    if (params.pointerReaction) {
      window.addEventListener("pointermove", onPointerMove, { passive: true });
    }

    /* ── resize ─────────────────────────────────────────────────────────── */
    const onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      if (composer) composer.setSize(w, h);
    };
    window.addEventListener("resize", onResize, { passive: true });

    /* ── the frame ──────────────────────────────────────────────────────── */
    const threeClock = new THREE.Clock();
    const sceneState = { orbOut: 0, galaxyIn: 0, galaxyOut: 0, galaxyDive: 0 };
    const frameInterval = 1000 / params.targetFps;
    let lastFrame = 0;
    let cycleTime = 0;
    let raf = 0;
    // Pointer sway is tracked apart from camera.position.x, because the focus
    // bias below is added on top of it every frame.
    let swayX = 0;
    let swayY = 0;

    const drawFrame = (now) => {
      raf = requestAnimationFrame(drawFrame);
      if (document.hidden) return;
      if (now - lastFrame < frameInterval - 1) return; // tier frame gate
      lastFrame = now;

      const delta = Math.min(threeClock.getDelta(), 0.05);
      const t = threeClock.getElapsedTime();

      cycleTime = (cycleTime + delta) % LOOP_TOTAL;
      const { progress, intro, outro, phase } = readLoop(cycleTime);
      sampleScene(progress, sceneState);
      const presence = galaxyPresence(sceneState);
      const handoff = 1 - outro;

      const w = window.innerWidth;
      const h = window.innerHeight;
      const pixelRatio = renderer.getPixelRatio();

      /* backdrop — palette crossfades on galaxy presence */
      backdropUniforms.iTime.value = t;
      backdropUniforms.uBg.value.lerpVectors(orbVecs.bg, galVecs.bg, presence);
      backdropUniforms.uFlameA.value.lerpVectors(orbVecs.flameA, galVecs.flameA, presence);
      backdropUniforms.uFlameB.value.lerpVectors(orbVecs.flameB, galVecs.flameB, presence);
      backdropUniforms.uFlameAmt.value = lerp(ORB_PALETTE.flameAmt, GALAXY_PALETTE.flameAmt, presence);

      /* orb */
      orbUniforms.uTime.value = t;
      orbUniforms.uPR.value = pixelRatio;
      orbUniforms.uAppear.value = handoff;
      orbUniforms.uAssemble.value = intro;
      orbUniforms.uOut.value = orbDissolve(sceneState);
      orbUniforms.uCore.value = orbCoreOpen(progress);

      orbPoints.rotation.y = t * ORB_CONFIG.spin;
      orbPoints.rotation.x = Math.sin(t * 0.1) * ORB_CONFIG.tilt;

      const approach = orbApproach(progress);
      orbGroup.position.z = params.orbMoveY * ORB_CONFIG.approach * approach;
      orbUniforms.uDeform.value = ORB_CONFIG.deform * (1 + approach * ORB_CONFIG.distortGain);

      // The bore and the spawn origin are object-space and the points spin, so
      // the camera has to be re-solved in local space AFTER the rotation.
      orbPoints.updateMatrixWorld();
      camLocal.copy(camera.position);
      orbPoints.worldToLocal(camLocal);
      orbUniforms.uCamLocal.value.copy(camLocal);
      orbCentre.set(0, orbGroup.position.y, 0);
      orbUniforms.uCentre.value.copy(orbCentre);

      oil.step(camera, pointer, orbCentre, params.pointerReaction, delta);
      orbUniforms.uCursor.value.copy(oil.cursor);
      orbUniforms.uCursorVel.value.copy(oil.velocity);
      orbUniforms.uEnergy.value = oil.energy;

      orbGroup.visible = intro > 0.001 && handoff > 0.002 && orbAlpha(sceneState) > 0.002;

      /* galaxy */
      const gAppear = galaxyAppear(sceneState);
      const gBlow = galaxyBlow(sceneState);
      const gAssemble = galaxyAssemble(sceneState);
      galaxyUniforms.uTime.value = t;
      galaxyUniforms.uAppear.value = gAppear;
      galaxyUniforms.uBlow.value = gBlow;
      galaxyUniforms.uFade.value = 1 - gBlow;
      galaxyUniforms.uAssemble.value = gAssemble;
      // Tip toward edge-on as the dive deepens. tilt is -0.5, so this reads
      // 0.5 → 0 across the dive. The sign matters: flip it and the disc tips
      // the wrong way and the camera falls out through the back of the arms.
      galaxyGroup.rotation.x = -(GALAXY_CONFIG.tilt + sceneState.galaxyDive * GALAXY_CONFIG.diveTilt);
      galaxyGroup.rotation.z = params.pointerReaction ? pointer.x * 0.12 : 0;

      // Cursor void: project the pointer onto the z = 0 plane in world space.
      galaxyCursorTarget.set(0, 0, 0);
      if (params.pointerReaction) {
        galaxyNdc.set(pointer.x, pointer.y, 0.5).unproject(camera);
        galaxyRay.copy(galaxyNdc).sub(camera.position).normalize();
        if (Math.abs(galaxyRay.z) > 1e-4) {
          const tPlane = -camera.position.z / galaxyRay.z;
          if (tPlane > 0 && Number.isFinite(tPlane)) {
            galaxyCursorTarget.copy(camera.position).addScaledVector(galaxyRay, tPlane);
          }
        }
      }
      galaxyCursor.lerp(galaxyCursorTarget, 0.12);
      galaxyUniforms.uCursor.value.copy(galaxyCursor);
      const wanted = params.pointerReaction ? 1 : 0;
      galaxyActivity += (wanted - galaxyActivity) * Math.min(1, delta * 4);
      galaxyUniforms.uActivity.value = galaxyActivity;

      // Stars stream in while `appear` is still small, so gate on assembly too
      // or the inbound dust is invisible.
      galaxyGroup.visible = (gAppear > 0.001 || gAssemble < 0.999) && gBlow < 0.999;

      /* brain */
      if (brainPoints) {
        const bAppear = brainAppear(progress);
        const exit = smoothstep(0.12, 1, outro); // dead-zone, then a soft burst
        brainUniforms.iTime.value = t;
        brainUniforms.iAlpha.value = bAppear * (1 - smoothstep(0.55, 1, outro));
        brainUniforms.uExplode.value = Math.max(1 - bAppear, exit);
        brainGroup.position.z = exit * BRAIN_CONFIG.approach;
        brainUniforms.iResolutionY.value = h * pixelRatio;
        brainUniforms.uAspect.value = w / Math.max(1, h);
        brainUniforms.uCursorRadius.value = clamp((0.1 * 4.6) / camera.position.length(), 0.04, 0.13);

        if (params.pointerReaction) {
          brainUniforms.uMouse.value.x += (pointer.x - brainUniforms.uMouse.value.x) * 0.18;
          brainUniforms.uMouse.value.y += (pointer.y - brainUniforms.uMouse.value.y) * 0.18;
        }
        const cursorTarget = params.pointerReaction ? 1 : 0;
        brainUniforms.uCursor.value += (cursorTarget - brainUniforms.uCursor.value) * 0.1;

        // The spin overlaps the assembly and runs right up to the burst — one
        // continuous move, no pause between gathering and turning.
        const spin = clamp((progress - 2.7) / 1.3, 0, 1) * BRAIN_CONFIG.scrollSpin;
        const cursorAmt = brainUniforms.uCursor.value;
        const targetRy = clamp(pointer.x, -1, 1) * BRAIN_CONFIG.cursorTilt * cursorAmt;
        const targetRx = -clamp(pointer.y, -1, 1) * BRAIN_CONFIG.cursorTilt * 0.65 * cursorAmt;
        brainParallax.ry += (targetRy - brainParallax.ry) * 0.05;
        brainParallax.rx += (targetRx - brainParallax.rx) * 0.05;
        brainGroup.rotation.y = BRAIN_CONFIG.baseRotationY + spin + brainParallax.ry;
        brainGroup.rotation.x = brainParallax.rx;

        brainGroup.visible = bAppear > 0.001 && outro < 0.998;
      }

      /* atmosphere — follows the camera, which travels z≈4 → 48 on the dive */
      atmoUniforms.uTime.value = t * 8;
      atmoUniforms.uRes.value.set(w * pixelRatio, h * pixelRatio);
      atmoUniforms.uColor.value.lerpVectors(atmoOrbColor, atmoGalaxyColor, presence);
      atmoUniforms.uAlpha.value = lerp(ORB_PALETTE.atmoAlpha, GALAXY_PALETTE.atmoAlpha, presence);
      atmoUniforms.uSpread.value = lerp(ORB_PALETTE.atmoSpread, GALAXY_PALETTE.atmoSpread, presence);
      atmoUniforms.uFadeNear.value = lerp(ORB_PALETTE.atmoFadeNear, GALAXY_PALETTE.atmoFadeNear, presence);
      atmoUniforms.uFadeFar.value = lerp(ORB_PALETTE.atmoFadeFar, GALAXY_PALETTE.atmoFadeFar, presence);
      atmosphere.position.copy(camera.position);

      /* camera rig */
      if (phase === "black") {
        // Nothing is drawn but the ambient wash, so the reset is unseen. Snap
        // rather than lerp, or the next cycle's dolly starts from the wrong place.
        camera.position.z = params.orbCameraZ + INTRO_DOLLY;
        swayX = 0;
        swayY = 0;
      } else {
        const eased = 1 - Math.pow(1 - intro, 2);
        const galaxyZ = GALAXY_CONFIG.cameraZ - sceneState.galaxyDive * GALAXY_CONFIG.dive;
        const targetZ = lerp(params.orbCameraZ, galaxyZ, presence) + (1 - eased) * INTRO_DOLLY;
        camera.position.z += (targetZ - camera.position.z) * Math.min(1, delta * 8);

        // Pointer sway: a whisper for the close orb, an orbit for the wide galaxy.
        const sway = lerp(ORB_PARALLAX, GALAXY_CONFIG.parallax, presence);
        const targetX = params.pointerReaction ? pointer.x * sway * eased : 0;
        const targetY = params.pointerReaction ? pointer.y * sway * 0.55 * eased : 0;
        const follow = Math.min(1, delta * 3);
        swayX += (targetX - swayX) * follow;
        swayY += (targetY - swayY) * follow;
      }

      // Lateral bias: move the camera and its aim target together, so the forms
      // shift across the frame with no perspective distortion. The offset is
      // derived from the frame width at the current distance, so the bias holds
      // constant in SCREEN terms whether the camera sits at z=3.8 or z=48 — a
      // fixed world offset would read as a big shift on the orb and none at all
      // on the galaxy.
      const focus = w >= 1024 ? focusX : 0.5;
      const frameWidth =
        2 * Math.tan(((camera.fov * Math.PI) / 180) / 2) *
        Math.abs(camera.position.z) * (w / Math.max(1, h));
      const shift = (0.5 - focus) * frameWidth;

      camera.position.x = swayX + shift;
      camera.position.y = swayY;
      camera.lookAt(shift, 0, 0);

      if (composer) composer.render();
      else renderer.render(scene, camera);
    };

    if (reduced) {
      // Reduced motion: settle on the orb and stop. One static frame, no loop.
      threeClock.getDelta();
      orbUniforms.uAssemble.value = 1;
      orbUniforms.uPR.value = renderer.getPixelRatio();
      orbGroup.visible = true;
      const focus = window.innerWidth >= 1024 ? focusX : 0.5;
      const frameWidth =
        2 * Math.tan(((camera.fov * Math.PI) / 180) / 2) *
        params.orbCameraZ * (window.innerWidth / Math.max(1, window.innerHeight));
      const shift = (0.5 - focus) * frameWidth;
      camera.position.set(shift, 0, params.orbCameraZ);
      camera.lookAt(shift, 0, 0);
      orbPoints.updateMatrixWorld();
      camLocal.copy(camera.position);
      orbPoints.worldToLocal(camLocal);
      orbUniforms.uCamLocal.value.copy(camLocal);
      atmosphere.position.copy(camera.position);
      if (composer) composer.render();
      else renderer.render(scene, camera);
    } else {
      raf = requestAnimationFrame(drawFrame);
    }

    /* ── cleanup ────────────────────────────────────────────────────────── */
    // React 18 StrictMode mounts effects twice in dev, so every resource created
    // above has to be released here or the second mount leaks a WebGL context.
    return () => {
      cancelAnimationFrame(raf);
      abort.abort();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onPointerMove);
      if (composer) composer.dispose();
      if (brainGeo) brainGeo.dispose();
      for (const item of disposables) item.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === host) host.removeChild(renderer.domElement);
    };
  }, [brainMeshUrl, quality, focusX]);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}
    />
  );
}
